import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import { ReactComponent as AngleUpIcon } from 'assets/angle-up.svg';

import { DESIGN_REMIX_STATUS } from 'utlis/constants';
import { getFeedbackDisplayNumber } from 'utlis/feedbackNumbering';

import CarouselRefinementPanel from './CarouselRefinementPanel';
import FeedbackPin from './FeedbackPin';
import { usePrototypePreview } from './PrototypePreviewContext';
import PrototypeSideBySidePanel from './PrototypeSideBySidePanel';

import styles from './styles.module.scss';

const CATEGORY_MAPPING = {
  error: { class: 'error' },
  suggestion: { class: 'warning' },
  positive: { class: 'positive' },
  'deeper-insights': { class: 'deeper-insights' },
};

const MAX_IMAGE_CONTENT_HEIGHT = 600;

const ImageCarousel = ({
  feedback,
  displayNumberMap,
  images,
  selectedImageIndex = 0,
  onImageIndexChange,
  onPinClick,
  isLoading = false,
  activeInsightUuid = null,
  isReviewOwner = false,
}) => {
  const [currentImageDimensions, setCurrentImageDimensions] = useState(null);
  // Rendered CSS pixels captured on load — locks both panels to the same size
  const [renderedImageDimensions, setRenderedImageDimensions] = useState(null);
  const [thumbnailsOverflow, setThumbnailsOverflow] = useState(false);
  const [showOriginalEntryAnimation, setShowOriginalEntryAnimation] = useState(false);
  const currentImageAspectRatio = currentImageDimensions
    ? currentImageDimensions.width / currentImageDimensions.height
    : null;
  const thumbnailsWrapperRef = useRef(null);
  const thumbnailsInnerRef = useRef(null);
  const imageRef = useRef(null);
  const rowContainerRef = useRef(null);
  const prevPanelCountRef = useRef(0);
  const panelRefs = useRef({});
  const prevRectsRef = useRef({});
  const prevPanelOrderRef = useRef([]);
  const prevDismissedKeysRef = useRef(new Set());
  const [areThumbnailsVisible, setAreThumbnailsVisible] = useState(true);
  const [isVariantDropdownOpen, setIsVariantDropdownOpen] = useState(false);
  const lastScrollTopRef = useRef(0);
  const variantDropdownRef = useRef(null);

  const { getPrototypeForPage, getPanelsForPage, prototypeMap } = usePrototypePreview();

  const currentPage = images[selectedImageIndex]?.page ?? selectedImageIndex + 1;
  const prototypeState = activeInsightUuid ? getPrototypeForPage(currentPage, activeInsightUuid) : null;
  const hasPrototype = Boolean(prototypeState);
  const prototypePanels = activeInsightUuid ? getPanelsForPage(currentPage, activeInsightUuid) : [];
  const prevHasPrototypeRef = useRef(hasPrototype);

  const activePrototypeEntry = activeInsightUuid ? prototypeMap[activeInsightUuid] : null;
  const isDismissingUuid = activePrototypeEntry?.isDismissingUuid ?? null;
  const isOwnerView = activePrototypeEntry?.isOwnerView ?? false;
  const onDismissVariant = activePrototypeEntry?.onDismissVariant ?? null;

  const onRefinePrototype = activePrototypeEntry?.onRefine ?? null;
  const isRefiningPrototype = activePrototypeEntry?.isRefining ?? false;
  const refineDisabledReason = activePrototypeEntry?.refineDisabledReason ?? null;
  const refinementErrorMessage = activePrototypeEntry?.refinementError ?? null;
  const showRefinementSlot = Boolean(
    isReviewOwner
    && onRefinePrototype
    && activePrototypeEntry?.status === DESIGN_REMIX_STATUS.COMPLETED
  );

  // Landscape = aspect ratio >= 1 (width >= height); portrait = ratio < 1.
  const isLandscape = currentImageAspectRatio !== null && currentImageAspectRatio !== undefined
    ? currentImageAspectRatio >= 1
    : null;

  const hasDismissedPanels = isOwnerView && prototypePanels.some(p => p.isDismissed);
  const firstDismissedIdx = hasDismissedPanels ? prototypePanels.findIndex(p => p.isDismissed) : -1;
  const dividerStyle = isLandscape === true
    ? { width: renderedImageDimensions?.width ?? 0 }
    : { height: renderedImageDimensions?.height ?? 0 };

  const activePanels = prototypePanels.filter(p => !p.isDismissed);
  const dismissedDropdownPanels = prototypePanels.filter(p => p.isDismissed);
  const showVariantDropdown = hasPrototype && prototypePanels.length >= 2;

  // Thumbnails move inside the stacked scroll container so the prototype is visible through them
  const isInsideStacked = hasPrototype && isLandscape === true;

  // Choose the layout class once to avoid a nested ternary in JSX
  const layoutClass = hasPrototype
    ? (
      isLandscape === true
        ? styles['image-row--stacked']
        : isLandscape === false
          ? styles['image-row--side-by-side']
          : 'flex-col gap-4'
    )
    : 'flex-col gap-4';

  const thumbnailIndices = useMemo(
    () => [...Array(images.length).keys()],
    [images.length]
  );

  // Detect if thumbnails exceed parent container width
  useEffect(() => {
    const wrapper = thumbnailsWrapperRef.current;
    const inner = thumbnailsInnerRef.current;

    if (!wrapper || !inner || !images.length) {
      setThumbnailsOverflow(false);

      return;
    }

    const checkOverflow = () => {
      const availableWidth = wrapper.clientWidth - 32;
      const contentWidth = inner.scrollWidth;

      setThumbnailsOverflow(contentWidth > Math.max(0, availableWidth));
    };

    const rafId = requestAnimationFrame(() => {
      checkOverflow();
    });

    // eslint-disable-next-line compat/compat -- ResizeObserver used for thumbnail overflow detection
    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(checkOverflow);
    });

    resizeObserver.observe(wrapper);

    resizeObserver.observe(inner);

    return () => {
      cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
    };
  }, [images.length, isInsideStacked]);

  useEffect(() => {
    setCurrentImageDimensions(null);
    setRenderedImageDimensions(null);
    setIsVariantDropdownOpen(false);

    if (rowContainerRef.current) {
      rowContainerRef.current.scrollTop = 0;
      rowContainerRef.current.scrollLeft = 0;
    }
  }, [selectedImageIndex]);

  const measureImage = useCallback(imgEl => {
    if (!imgEl) {
      return;
    }

    const { naturalWidth, naturalHeight } = imgEl;

    setCurrentImageDimensions({ width: naturalWidth, height: naturalHeight });

    // Use requestAnimationFrame to ensure DOM has fully updated
    requestAnimationFrame(() => {
      const rect = imgEl.getBoundingClientRect();

      // Get the container's available space to cap the dimensions
      const rowEl = rowContainerRef.current;
      const availableW = Math.max((rowEl?.clientWidth ?? 0) - 32, 0);
      const availableH = Math.min(
        Math.max((rowEl?.clientHeight ?? 0) - 56, 0), // 56 = padding + header
        MAX_IMAGE_CONTENT_HEIGHT
      );

      if (rect.width > 0 && rect.height > 0) {
        // Cap the dimensions to the container's available space while maintaining aspect ratio
        let finalWidth = rect.width;
        let finalHeight = rect.height;

        // If height needs to be capped, scale width proportionally
        if (availableH > 0 && rect.height > availableH) {
          finalHeight = availableH;
          finalWidth = (rect.width / rect.height) * availableH;
        }

        // If width still exceeds available space, scale down further
        if (availableW > 0 && finalWidth > availableW) {
          finalWidth = availableW;
          finalHeight = (rect.height / rect.width) * availableW;
        }

        setRenderedImageDimensions({
          width: Math.round(finalWidth),
          height: Math.round(finalHeight),
        });
      }
      else {
        // Fallback: calculate from container dimensions
        const availableW1 = Math.max((rowEl?.clientWidth ?? 0) - 32, 0);
        const availableH1 = Math.min(
          Math.max((rowEl?.clientHeight ?? 0) - 32, 0),
          MAX_IMAGE_CONTENT_HEIGHT
        );

        if (availableW1 > 0 && availableH1 > 0 && naturalWidth > 0 && naturalHeight > 0) {
          const fitScale = Math.min(
            availableW1 / naturalWidth,
            availableH1 / naturalHeight,
            1
          );

          const calculatedWidth = Math.round(naturalWidth * fitScale);
          const calculatedHeight = Math.round(naturalHeight * fitScale);

          // Only set if dimensions are reasonable (not too small)
          if (calculatedWidth >= 100 && calculatedHeight >= 100) {
            setRenderedImageDimensions({
              width: calculatedWidth,
              height: calculatedHeight,
            });
          }
        }
      }
    });
  }, []);

  useEffect(() => {
    const imgEl = imageRef.current;

    if (imgEl && imgEl.complete && imgEl.naturalWidth > 0) {
      measureImage(imgEl);
    }
  }, [selectedImageIndex, measureImage]);

  useEffect(() => {
    setShowOriginalEntryAnimation(false);
    prevHasPrototypeRef.current = hasPrototype;
  }, [selectedImageIndex]);

  useEffect(() => {
    if (hasPrototype && !prevHasPrototypeRef.current) {
      setShowOriginalEntryAnimation(true);
    }
    else if (!hasPrototype) {
      setShowOriginalEntryAnimation(false);
    }

    prevHasPrototypeRef.current = hasPrototype;
  }, [hasPrototype]);

  // Scroll the row container so the in-progress generation panel is visible when it first appears.
  useEffect(() => {
    const panelCount = prototypePanels.length;
    const lastPanel = prototypePanels[panelCount - 1];
    const isNewInProgressPanel
      = lastPanel?.status === DESIGN_REMIX_STATUS.IN_PROGRESS
      && panelCount > prevPanelCountRef.current;

    prevPanelCountRef.current = panelCount;

    if (!isNewInProgressPanel || isLandscape === null) {
      return;
    }

    requestAnimationFrame(() => {
      const container = rowContainerRef.current;

      if (!container) {
        return;
      }

      if (isLandscape) {
        // Stacked layout (column): new panel is below — scroll down
        const maxScroll = container.scrollHeight - container.clientHeight;

        if (maxScroll > 0 && container.scrollTop < maxScroll) {
          container.scrollTo({ top: maxScroll, behavior: 'smooth' });
        }
      }
      else {
        // Side-by-side layout (row): new panel is to the right — scroll right
        const maxScroll = container.scrollWidth - container.clientWidth;

        if (maxScroll > 0 && container.scrollLeft < maxScroll) {
          container.scrollTo({ left: maxScroll, behavior: 'smooth' });
        }
      }
    });
  }, [prototypePanels, isLandscape]);

  // Hide thumbnails on scroll-down, reveal on scroll-up (stacked landscape layout only)
  useEffect(() => {
    setAreThumbnailsVisible(true);

    if (!isInsideStacked) {
      return;
    }

    const container = rowContainerRef.current;

    if (!container) {
      return;
    }

    lastScrollTopRef.current = container.scrollTop;

    const handleScroll = () => {
      const current = container.scrollTop;
      const prev = lastScrollTopRef.current;

      if (current <= 0) {
        setAreThumbnailsVisible(true);
      }
      else if (current > prev) {
        setAreThumbnailsVisible(false);
      }
      else if (current < prev) {
        setAreThumbnailsVisible(true);
      }

      lastScrollTopRef.current = Math.max(0, current);
    };

    container.addEventListener('scroll', handleScroll, { passive: true });

    return () => container.removeEventListener('scroll', handleScroll);
  }, [isInsideStacked, selectedImageIndex]);

  useEffect(() => {
    if (!isVariantDropdownOpen) {
      return;
    }

    const handleClickOutside = e => {
      if (variantDropdownRef.current && !variantDropdownRef.current.contains(e.target)) {
        setIsVariantDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isVariantDropdownOpen]);

  // animate panels to new positions when their order changes (e.g. dismiss/restore)
  useLayoutEffect(() => {
    const currentOrder = prototypePanels.map(p => p.key);
    const prevOrder = prevPanelOrderRef.current;

    const currentDismissedKeys = new Set(
      prototypePanels.filter(p => p.isDismissed).map(p => p.key)
    );
    const prevDismissedKeys = prevDismissedKeysRef.current;

    const orderChanged = currentOrder.length !== prevOrder.length
      || currentOrder.some((key, i) => key !== prevOrder[i]);
    const dismissStateChanged = [...currentDismissedKeys].some(k => !prevDismissedKeys.has(k))
      || [...prevDismissedKeys].some(k => !currentDismissedKeys.has(k));

    prevPanelOrderRef.current = currentOrder;
    prevDismissedKeysRef.current = currentDismissedKeys;

    if (!orderChanged && !dismissStateChanged) {
      return;
    }

    const oldRects = prevRectsRef.current;
    // ease-in-out distributes motion evenly so the slide is visible throughout
    const FADE_OUT_MS = 200;
    const SPRING_MS = 700;
    const SPRING_EASE = 'cubic-bezier(0.42, 0, 0.58, 1)';

    const springEnter = el => {
      el.style.transition = 'none';
      el.style.transform = 'translateY(20px)';
      el.style.opacity = '0';
      void el.getBoundingClientRect();
      el.style.transition = `transform ${SPRING_MS}ms ${SPRING_EASE}, opacity 350ms ease-out`;
      el.style.transform = 'translateY(0px)';
      el.style.opacity = '1';
    };

    const flipSlide = (el, dx, dy) => {
      el.style.transition = 'none';
      el.style.transform = `translate(${dx}px, ${dy}px)`;
      void el.getBoundingClientRect();
      el.style.transition = `transform ${SPRING_MS}ms ${SPRING_EASE}`;
      el.style.transform = '';
    };

    prototypePanels.forEach(panel => {
      const el = panelRefs.current[panel.key];

      if (!el) {
        return;
      }

      const oldRect = oldRects[panel.key];
      const justDismissed = panel.isDismissed && !prevDismissedKeys.has(panel.key);
      const justRestored = !panel.isDismissed && prevDismissedKeys.has(panel.key);

      if (justDismissed && oldRect) {
        // hold at old (active) position and fade out
        const newRect = el.getBoundingClientRect();
        const dx = Math.round(oldRect.left - newRect.left);
        const dy = Math.round(oldRect.top - newRect.top);

        el.style.transition = 'none';
        el.style.transform = `translate(${dx}px, ${dy}px)`;
        el.style.opacity = '1';
        void el.getBoundingClientRect();
        el.style.transition = `opacity ${FADE_OUT_MS}ms ease-out`;
        el.style.opacity = '0';

        // spring enter at new (dismissed) position
        setTimeout(() => {
          const target = panelRefs.current[panel.key];

          if (target) {
            springEnter(target);
          }
        }, FADE_OUT_MS + 50);
      }
      else if (justDismissed || justRestored) {
        // No prior position (justDismissed) or put back — spring enter at current position
        springEnter(el);
      }
      else if (oldRect) {
        const newRect = el.getBoundingClientRect();
        const dx = Math.round(oldRect.left - newRect.left);
        const dy = Math.round(oldRect.top - newRect.top);

        if (dx !== 0 || dy !== 0) {
          flipSlide(el, dx, dy);
        }
      }
    });
  }, [prototypePanels]);

  // Capture positions after every render so the FLIP effect always has fresh "before" rects
  useLayoutEffect(() => {
    const rects = {};

    Object.keys(panelRefs.current).forEach(key => {
      const el = panelRefs.current[key];

      if (el) {
        rects[key] = el.getBoundingClientRect();
      }
    });

    prevRectsRef.current = rects;
  });

  // Get feedback items for current page with pin locations, handling dismissed findings and overlapping positions
  const currentPageFeedback = useMemo(() => {
    if (!feedback || !Array.isArray(feedback) || !images[selectedImageIndex]) {
      return [];
    }

    const currentImagePage = images[selectedImageIndex].page || selectedImageIndex + 1;

    // Filter for current page, exclude dismissed findings, and only include items with pin locations
    const activeFeedback = feedback.filter(
      item => item.page === currentImagePage
      && !item.dismiss
      && item.dismiss !== true
      && item.pin_location
    );

    // Handle overlapping pin positions
    const positionTracker = new Map();

    return activeFeedback.map(feedbackItem => {
      const pinLocation = feedbackItem.pin_location;

      const positionKey = `${pinLocation.x_percent}-${pinLocation.y_percent}`;

      // Find the original index of this feedback item in the full feedback array
      const originalIndex = feedback.findIndex(item => item === feedbackItem);
      const feedbackNumber = getFeedbackDisplayNumber(
        feedbackItem,
        displayNumberMap,
        feedbackItem.index ?? originalIndex
      );

      // Determine category and severity class (matching FeedbackBox logic)
      const getCategory = item => {
        if (item.isDeeperInsight) {
          return 'deeper-insights';
        }

        return item.category || '';
      };

      const category = getCategory(feedbackItem);
      const severityClass = category && CATEGORY_MAPPING[category]
        ? CATEGORY_MAPPING[category].class
        : '';

      if (positionTracker.has(positionKey)) {
        // If position is already used, offset the new pin to the right
        const offset = positionTracker.get(positionKey) * 8; // 5% offset for each overlapping pin

        positionTracker.set(positionKey, positionTracker.get(positionKey) + 1);

        return {
          ...feedbackItem,
          feedbackIndex: originalIndex,
          feedbackNumber,
          severityClass,
          adjustedLocation: {
            x_percent: Math.min(pinLocation.x_percent + offset, 95), // Don't go beyond 95% to avoid edge cutoff
            y_percent: pinLocation.y_percent,
          },
        };
      }
      positionTracker.set(positionKey, 1);

      return {
        ...feedbackItem,
        feedbackIndex: originalIndex,
        feedbackNumber,
        severityClass,
        adjustedLocation: pinLocation,
      };
    });
  }, [feedback, displayNumberMap, selectedImageIndex, images]);

  const handleThumbnailClick = index => {
    // Allow clicking on any page index
    handleImageSelect(index);
  };

  const handleNavigateToVariant = panelKey => {
    const el = panelRefs.current[panelKey];

    if (!el) {
      return;
    }

    el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    setIsVariantDropdownOpen(false);
  };

  if (isLoading) {
    return (
      <div className={cn(styles['detail-image-wrapper'])}>
        <div className={styles['detail-image-placeholder']}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}
          >
            <div
              style={{
                width: '16px',
                height: '16px',
                border: '2px solid #E2E8F0',
                borderTop: '2px solid #3B82F6',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
              }}
            />
            Loading images...
          </div>
        </div>
      </div>
    );
  }

  if (!images || images.length === 0) {
    return (
      <div className={cn(styles['detail-image-wrapper'])}>
        <div className={styles['detail-image-placeholder']}>
          No images available
        </div>
      </div>
    );
  }

  const safeIndex = selectedImageIndex >= 0 && selectedImageIndex < images.length
    ? selectedImageIndex
    : 0;
  const currentImage = images[safeIndex];

  const thumbnailsEl = (
    <div
      ref={thumbnailsWrapperRef}
      className={cn(styles['detail-thumbnails-wrapper'], {
        [styles['detail-thumbnails-wrapper--overlay']]: isInsideStacked,
        [styles['detail-thumbnails-wrapper--scrollable']]: thumbnailsOverflow,
        [styles['detail-thumbnails-wrapper--hidden']]: isInsideStacked && !areThumbnailsVisible,
      })}
    >
      <div
        ref={thumbnailsInnerRef}
        className={cn(styles['detail-thumbnails'], {
          [styles['detail-thumbnails--scrollable']]: thumbnailsOverflow,
        })}
      >
        {thumbnailIndices.map(index => {
          const image = images[index];
          const isSelected = index === safeIndex;

          return (
            <button
              key={index}
              type="button"
              className={cn(styles['detail-thumbnail-item'], {
                [styles['detail-thumbnail-item--selected']]: isSelected,
              })}
              onClick={() => handleThumbnailClick(index)}
              aria-label={`Go to image ${index + 1}`}
              aria-current={isSelected ? 'true' : undefined}
            >
              <div
                className={cn(styles['detail-thumbnail-box'], {
                  [styles['detail-thumbnail-box--selected']]: isSelected,
                })}
              >
                <img
                  src={image.url}
                  alt={image.alt || `Design ${index + 1}`}
                  className={styles['detail-thumbnail-img']}
                  loading="lazy"
                />
              </div>
              <div className={styles['detail-thumbnail-number']}>
                {index + 1}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className={cn('flex flex-1 flex-col gap-4', isInsideStacked ? 'overflow-hidden relative' : 'overflow-auto')}>
      <div
        ref={rowContainerRef}
        className={cn(
          'flex flex-1 min-h-0',
          layoutClass,
          !hasPrototype && 'items-center justify-center',
          isInsideStacked && styles['image-row--stacked--thumbnails-inside']
        )}
      >
        <div
          className={cn(
            styles['detail-image-wrapper'],
            hasPrototype && isLandscape === false && styles['detail-image-wrapper--with-prototype'],
            hasPrototype && isLandscape === true && styles['detail-image-wrapper--with-prototype-landscape'],
            showOriginalEntryAnimation && isLandscape === false && styles['original-slide-to-left'],
            showOriginalEntryAnimation && isLandscape === true && styles['original-slide-to-top']
          )}
          style={
            (hasPrototype
              && renderedImageDimensions
              && renderedImageDimensions.width >= 100
              && renderedImageDimensions.height >= 100)
              ? {
                width: renderedImageDimensions.width + 32,
                height: renderedImageDimensions.height + 40,
                flexShrink: 0,
              }
              : {}
          }
        >
          {hasPrototype && (
            <div className={styles['original-panel-header']}>
              <p className={cn(styles['screen-label'], styles['screen-label--original'])}>
                Original
              </p>
              {showVariantDropdown && (
                <div
                  ref={variantDropdownRef}
                  className={styles['variant-nav-dropdown']}
                >
                  <button
                    type="button"
                    className={cn(
                      styles['variant-nav-trigger'],
                      isVariantDropdownOpen && styles['variant-nav-trigger--open']
                    )}
                    onClick={() => setIsVariantDropdownOpen(prev => !prev)}
                    aria-haspopup="listbox"
                    aria-expanded={isVariantDropdownOpen}
                  >
                    Variants
                    <AngleUpIcon
                      className={cn(
                        styles['variant-nav-caret'],
                        !isVariantDropdownOpen && styles['variant-nav-caret--closed']
                      )}
                    />
                  </button>
                  {isVariantDropdownOpen && (
                    <div
                      className={styles['variant-nav-menu']}
                      role="listbox"
                      aria-label="Navigate to variant"
                    >
                      {activePanels.map(panel => (
                        <button
                          key={panel.key}
                          type="button"
                          role="option"
                          className={styles['variant-nav-item']}
                          onClick={() => handleNavigateToVariant(panel.key)}
                        >
                          {`Variant ${String.fromCharCode(65 + (panel.variantIndex ?? 0))}`}
                        </button>
                      ))}
                      {dismissedDropdownPanels.length > 0 && (
                        <>
                          {activePanels.length > 0 && (
                            <div className={styles['variant-nav-divider']} />
                          )}
                          <p className={styles['variant-nav-section-label']}>
                            Dismissed
                          </p>
                          {dismissedDropdownPanels.map(panel => (
                            <button
                              key={panel.key}
                              type="button"
                              role="option"
                              className={styles['variant-nav-item']}
                              onClick={() => handleNavigateToVariant(panel.key)}
                            >
                              {`Variant ${String.fromCharCode(65 + (panel.variantIndex ?? 0))}`}
                            </button>
                          ))}
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <div
            className={styles['inner']}
            style={
              renderedImageDimensions
                ? { width: renderedImageDimensions.width, height: renderedImageDimensions.height }
                : undefined
            }
          >
            <img
              ref={imageRef}
              key={safeIndex}
              src={currentImage.url}
              alt={currentImage.alt || `Design ${safeIndex + 1}`}
              className={cn(
                styles['detail-image'],
                hasPrototype && styles['detail-image--canvas']
              )}
              loading={safeIndex <= 1 ? 'eager' : 'lazy'}
              onLoad={e => measureImage(e.target)}
            />
            {currentPageFeedback.map((feedbackItem, index) => (
              <FeedbackPin
                key={`${feedbackItem.id || index}-pin`}
                number={feedbackItem.feedbackNumber}
                x={feedbackItem.adjustedLocation.x_percent}
                y={feedbackItem.adjustedLocation.y_percent}
                severity={feedbackItem.severityClass}
                onClick={
                  onPinClick && feedbackItem.feedbackIndex != null
                    ? () => onPinClick(feedbackItem.feedbackIndex)
                    : undefined
                }
              />
            ))}
          </div>
        </div>
        {prototypePanels.map((panel, idx) => {
          const panelKey = panel.key || `panel-${idx}`;
          const showDividerBefore = hasDismissedPanels
              && renderedImageDimensions
              && idx === firstDismissedIdx;

          return (
            <React.Fragment key={panelKey}>
              {showDividerBefore && (
                <div
                  className={cn(
                    styles['variant-group-divider'],
                    isLandscape === true && styles['variant-group-divider--landscape']
                  )}
                  style={dividerStyle}
                />
              )}
              <div
                ref={el => {
                  if (el) {
                    panelRefs.current[panelKey] = el;
                  }
                  else {
                    delete panelRefs.current[panelKey];
                  }
                }}
                style={{ flexShrink: 0 }}
              >
                <PrototypeSideBySidePanel
                  remixStatus={panel.status}
                  previewSrc={panel.previewSrc}
                  htmlUrl={panel.htmlUrl}
                  processUuid={panel.processUuid}
                  variantIndex={panel.variantIndex ?? idx}
                  refinementPrompt={panel.refinementPrompt}
                  isDismissed={panel.isDismissed}
                  isDismissing={isDismissingUuid === panel.processUuid}
                  isOwnerView={isOwnerView}
                  isReviewOwner={isReviewOwner}
                  onDismiss={onDismissVariant}
                  renderedDimensions={renderedImageDimensions}
                  isLandscape={isLandscape === true}
                  onGenerate={panel.onGenerate}
                  isGenerateDisabled={panel.isGenerateDisabled}
                  generateDisabledReason={panel.generateDisabledReason}
                  isGenerateBusy={panel.isGenerateBusy}
                  ctaTitle={panel.ctaTitle}
                  ctaSubtitle={panel.ctaSubtitle}
                  ctaButtonLabel={panel.ctaButtonLabel}
                />
              </div>
            </React.Fragment>
          );
        })}
        {showRefinementSlot && (
          <CarouselRefinementPanel
            onSubmit={onRefinePrototype}
            isSubmitting={isRefiningPrototype}
            disabled={Boolean(refineDisabledReason)}
            disabledReason={refineDisabledReason}
            isLandscape={isLandscape === true}
            renderedDimensions={renderedImageDimensions}
            errorMessage={refinementErrorMessage}
          />
        )}
      </div>
      {thumbnailsEl}
    </div>
  );

  // Handle image selection to show relevant feedback
  function handleImageSelect(imageIndex) {
    if (onImageIndexChange) {
      onImageIndexChange(imageIndex);
    }
  }
};

ImageCarousel.propTypes = {
  feedback: PropTypes.array.isRequired,
  displayNumberMap: PropTypes.object,
  images: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    url: PropTypes.string.isRequired,
    page: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    alt: PropTypes.string,
  })),
  selectedImageIndex: PropTypes.number,
  onImageIndexChange: PropTypes.func,
  onPinClick: PropTypes.func,
  isLoading: PropTypes.bool,
  activeInsightUuid: PropTypes.string,
  isReviewOwner: PropTypes.bool,
};

ImageCarousel.defaultProps = {
  feedback: [],
  images: [],
};

export default ImageCarousel;
